import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { EngagementBuilderDialog } from '../../Compounds/Advise/EngagementBuilderDialog';
import { ConfirmDialog } from '../../Compounds/Advise/ConfirmDialog';
import { EngagementsPageGridColumnDefs } from './EngagementsPageGridColumnDefs';
import { AdviseEngagement, AdviseCustomer, AdviseSupplier } from '../../../types/advise';
import {
  getAdviseEngagementSubject,
  getAdviseEngagementLoadingSubject,
  useAdviseEngagements
} from '../../../store/AdviseEngagement';
import { getAdviseCustomerSubject, useAdviseCustomers } from '../../../store/AdviseCustomer';
import { getAdviseSupplierSubject, useAdviseSuppliers } from '../../../store/AdviseSupplier';
import { getSelectedAccountsSubject, hasAdviseFull } from '../../../store/User';
import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { AdviseAccountGuard } from '../../Compounds/Advise/AdviseAccountGuard';
import { NewEngagementButton } from '../../Compounds/Advise/NewEngagementButton';
import { NewEngagementDialog } from '../../Compounds/Advise/NewEngagementDialog/NewEngagementDialog';

export const AdviseEngagementsPage = () => {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState<AdviseEngagement[]>([]);
  const [customers, setCustomers] = useState<AdviseCustomer[]>([]);
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [newEngagementOpen, setNewEngagementOpen] = useState(false);
  const [editingEngagement, setEditingEngagement] = useState<AdviseEngagement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdviseEngagement | null>(null);

  const { fetchEngagements, updateEngagement, deleteEngagement } = useAdviseEngagements();
  const { fetchCustomers } = useAdviseCustomers();
  const { fetchSuppliers } = useAdviseSuppliers();

  useEffect(() => {
    const engagementSub = getAdviseEngagementSubject().subscribe((data) => setEngagements(data));
    const loadingSub = getAdviseEngagementLoadingSubject().subscribe((val) => setLoading(val));
    const customerSub = getAdviseCustomerSubject().subscribe((data) => setCustomers(data));
    const supplierSub = getAdviseSupplierSubject().subscribe((data) => setSuppliers(data));

    fetchEngagements();
    fetchCustomers();
    fetchSuppliers();

    const accountSub = getSelectedAccountsSubject().subscribe(() => {
      fetchEngagements(true);
      fetchCustomers(true);
    });

    return () => {
      engagementSub.unsubscribe();
      loadingSub.unsubscribe();
      customerSub.unsubscribe();
      supplierSub.unsubscribe();
      accountSub.unsubscribe();
    };
  }, []);

  const handleEdit = (engagement: AdviseEngagement) => {
    setEditingEngagement(engagement);
    setShowBuilder(true);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteEngagement(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const handleViewAnalysis = (engagement: AdviseEngagement) => {
    navigate(`/advise/analysis?id=${engagement.id}`);
  };

  const canEdit = hasAdviseFull();

  const customerLookup = new Map(customers.map((c) => [c.id, c.company_name]));

  const actionsColumn: GridSingleSelectColDef = {
    field: 'actions',
    headerName: '',
    sortable: false,
    width: 200,
    filterable: false,
    type: 'singleSelect',
    valueOptions: [],
    hideable: false,
    renderCell: (params: GridCellParams) => {
      const row = params.row as AdviseEngagement;
      return (
        <div className="flex gap-2">
          <button
            className="text-sm text-blue-600 hover:underline"
            onClick={() => handleViewAnalysis(row)}>
            Analyze
          </button>
          {canEdit && (
            <>
              <button
                className="text-sm text-blue-600 hover:underline"
                onClick={() => handleEdit(row)}>
                Edit
              </button>
              <button
                className="text-sm text-red-600 hover:underline"
                onClick={() => setDeleteTarget(row)}>
                Delete
              </button>
            </>
          )}
        </div>
      );
    }
  };

  const customerColumn: GridSingleSelectColDef = {
    field: 'customer_id',
    headerName: 'Customer',
    sortable: true,
    width: 180,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value: string) => customerLookup.get(value) || '',
    description: 'Drag to move column'
  };

  // TODO(UPDEV-8043): insert customerColumn by field name, not hard-coded slice indices — a column reorder silently breaks this.
  const columns = [
    actionsColumn,
    ...EngagementsPageGridColumnDefs.slice(0, 2),
    customerColumn,
    ...EngagementsPageGridColumnDefs.slice(2)
  ];

  const pageActions = <NewEngagementButton onClick={() => setNewEngagementOpen(true)} />;

  return (
    <PageLayout
      pageTitle="Advise Engagements"
      pageActions={pageActions}
      dataCy="advise-engagements-page">
      <AdviseSubNav />
      <AdviseAccountGuard>
        <PageCard
          title="Engagements"
          childrenWrapperClass="min-h-80 min-w-full">
          <UpstackDataGrid
            dataCy="advise-engagements-grid"
            title="Engagements"
            rows={engagements}
            columns={columns}
            loadingData={loading}
            page={'advise_engagement_grid'}
            gridSettings={{ muiConfig: {}, disabledFields: [] }}
            showSearch={true}
          />
        </PageCard>

        {editingEngagement && (
          <EngagementBuilderDialog
            open={showBuilder}
            onClose={() => setShowBuilder(false)}
            onSave={(engagementData) => updateEngagement(editingEngagement.id, engagementData)}
            engagement={editingEngagement}
            customers={customers}
            suppliers={suppliers}
          />
        )}

        <ConfirmDialog
          open={!!deleteTarget}
          title="Delete Engagement"
          message={`Are you sure you want to delete "${deleteTarget?.engagement_name}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      </AdviseAccountGuard>
      <NewEngagementDialog
        open={newEngagementOpen}
        onCancel={() => setNewEngagementOpen(false)}
        // The new engagement can be on any account, so "Configure later" lands on the cross-account dashboard.
        onConfigureLater={() => navigate('/advise')}
      />
    </PageLayout>
  );
};
